import { motion } from 'framer-motion';

export function MetricCard({ label, value, sub, icon: Icon, color = '#c9a84c', delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.24, delay: Math.min(delay, 6) * 0.04, ease: [0.2, 0.8, 0.2, 1] }}
      className="group relative rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 overflow-hidden hover:border-[#c9a84c]/20 transition-colors duration-300"
      data-testid={`metric-${label}`}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs text-gray-400 uppercase tracking-wider font-medium">{label}</span>
          {Icon && (
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}10` }}>
              <Icon size={16} style={{ color }} strokeWidth={2} />
            </div>
          )}
        </div>
        <p className="text-2xl font-bold text-white tracking-tight tabular-nums">{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-1.5">{sub}</p>}
      </div>
    </motion.div>
  );
}

export function SectionHeader({ title, subtitle, action }) {
  return (
    <div className="flex items-center justify-between mb-8">
      <div>
        <h2 className="text-2xl font-bold text-white tracking-tight">{title}</h2>
        {subtitle && <p className="text-sm text-gray-400 mt-1">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function StatusBadge({ status }) {
  const colors = {
    'Completada': 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    'Completado': 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    'Pagada': 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    'Activo': 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    'En Venta': 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    'En Progreso': 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    'Pendiente': 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    'Vendido': 'bg-gray-500/10 text-gray-400 border-gray-500/20',
    'Inactivo': 'bg-gray-500/10 text-gray-400 border-gray-500/20',
    'No Iniciado': 'bg-gray-500/10 text-gray-400 border-gray-500/20',
    'Proximamente': 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    'Próximamente': 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    'Exigible': 'bg-amber-500/10 text-amber-300 border-amber-500/20',
    'No exigible': 'bg-gray-500/10 text-gray-400 border-gray-500/20',
    'Reserva': 'bg-purple-500/10 text-purple-300 border-purple-500/20',
    'Baja': 'bg-red-500/10 text-red-300 border-red-500/20',
    'Retirada': 'bg-gray-500/10 text-gray-400 border-gray-500/20',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider border ${colors[status] || 'bg-gray-500/10 text-gray-400 border-gray-500/20'}`}>
      {status}
    </span>
  );
}

export function PremiumTable({ cols, rows, emptyText = 'Sin datos' }) {
  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/[0.06]">
              {cols.map((c, i) => (
                <th key={i} scope="col" className="text-left py-3.5 px-5 text-[11px] uppercase tracking-wider text-gray-400 font-semibold">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={cols.length} className="py-12 px-6 text-center text-gray-400 text-sm">{emptyText}</td></tr>
            ) : rows.map((r, i) => (
              <tr key={i} className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors duration-150">
                {r.map((cell, j) => (
                  <td key={j} className={`py-3.5 px-5 tabular-nums ${j === 0 ? 'text-white font-medium' : 'text-gray-300'}`}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function FilterPills({ options, active, onChange }) {
  return (
    <div className="flex gap-1.5 flex-wrap">
      {options.map(s => (
        <button
          key={s}
          onClick={() => onChange(s)}
          className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
            active === s
              ? 'bg-[#c9a84c] text-[#0a1628] shadow-lg shadow-[#c9a84c]/15'
              : 'bg-white/[0.04] text-gray-400 hover:text-gray-200 hover:bg-white/[0.06] border border-white/[0.06]'
          }`}
        >
          {s}
        </button>
      ))}
    </div>
  );
}

// Estado vacío con siguiente paso (auditoría UX: "Aún no tienes contactos: comparte tu enlace")
export function EmptyState({ icon: Icon, title, text, action }) {
  return (
    <div className="rounded-2xl border border-dashed border-white/[0.08] bg-white/[0.01] px-6 py-10 text-center" data-testid="empty-state">
      {Icon && (
        <div className="w-11 h-11 mx-auto mb-4 rounded-full bg-[#c9a84c]/10 flex items-center justify-center">
          <Icon size={18} className="text-[#c9a84c]" />
        </div>
      )}
      <p className="text-white font-medium">{title}</p>
      {text && <p className="text-sm text-gray-400 mt-1.5 max-w-md mx-auto leading-relaxed">{text}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

// Error de carga (para no confundirlo con un estado vacío)
export function LoadError({ onRetry, text = 'No hemos podido cargar los datos.' }) {
  return (
    <div role="alert" className="rounded-2xl border border-white/[0.06] bg-white/[0.02] px-6 py-10 text-center" data-testid="load-error">
      <p className="text-sm text-gray-300">{text}</p>
      {onRetry && (
        <button type="button" onClick={onRetry} className="mt-4 inline-flex rounded-xl bg-[#c9a84c] px-4 py-2 text-sm font-semibold text-[#0a1628] hover:bg-[#d8b95d] transition-colors">
          Reintentar
        </button>
      )}
    </div>
  );
}

// Esqueleto de carga (en lugar de fundidos lentos o un spinner a pantalla vacía)
export function PageSkeleton({ cards = 4, rows = 4 }) {
  return (
    <div className="space-y-8 animate-pulse" aria-busy="true" aria-label="Cargando" data-testid="page-skeleton">
      <div className="space-y-2">
        <div className="h-6 w-56 rounded bg-white/[0.06]" />
        <div className="h-4 w-80 max-w-full rounded bg-white/[0.04]" />
      </div>
      {cards > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: cards }).map((_, i) => <div key={i} className="h-28 rounded-2xl bg-white/[0.04]" />)}
        </div>
      )}
      <div className="rounded-2xl border border-white/[0.06] p-5 space-y-3">
        {Array.from({ length: rows }).map((_, i) => <div key={i} className="h-5 rounded bg-white/[0.04]" />)}
      </div>
    </div>
  );
}
